import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Field, Input, PasswordInput } from "@/components/base/FormControls";
import Alert from "@/components/base/Alert";
import { BRAND } from "@/config/brand";
import { supabase } from "@/store/shared/supabase";
import { useSession } from "@/session/SessionProvider";
import { homePathForUser } from "@/session/types";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState("");
  const [resetState, setResetState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [resetMessage, setResetMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: { email?: string; password?: string } = {};
    if (!/^\S+@\S+\.\S+$/.test(email)) next.email = "Veuillez saisir une adresse e-mail valide.";
    if (password.length < 6) next.password = "Le mot de passe doit contenir au moins 6 caractères.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    setFormError("");
    const result = await login(email, password, remember);
    setSubmitting(false);
    if (!result.ok) {
      setFormError(result.error);
      return;
    }
    navigate(homePathForUser(result.user), { replace: true });
  };

  // Réinitialisation du mot de passe : même mécanisme Supabase Auth que la page
  // de définition (aucun second système, aucun jeton créé côté application).
  const onForgotPassword = async () => {
    if (resetState === "sending") return;
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErrors((prev) => ({ ...prev, email: "Veuillez saisir une adresse e-mail valide." }));
      return;
    }
    setResetState("sending");
    setResetMessage("");
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${BRAND.domain}/reinitialiser-mot-de-passe`,
    });
    if (error) {
      setResetState("error");
      setResetMessage("L'envoi du lien de réinitialisation a échoué. Réessayez dans quelques instants.");
      return;
    }
    setResetState("sent");
    setResetMessage(
      "Si un compte existe avec cette adresse, un e-mail contenant un lien de définition du mot de passe vient d'être envoyé."
    );
  };

  return (
    <div className="grid min-h-[calc(100vh-104px)] grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center px-4 py-14 md:px-8">
        <div className="animate-fade-up w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 font-heading text-sm font-bold text-background-50">
              {BRAND.initials}
            </span>
            <span className="font-heading text-base font-semibold text-foreground-950">{BRAND.name}</span>
          </Link>

          <h1 className="mt-8 text-2xl md:text-3xl font-heading font-semibold text-foreground-950">Connexion</h1>
          <p className="mt-2 text-sm text-foreground-600">
            Accédez à votre espace Propriétaire, Agence ou Super Admin.
          </p>

          {formError && (
            <Alert tone="error" className="mt-5" title="Connexion impossible">
              {formError}
            </Alert>
          )}

          <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <Field label="E-mail" htmlFor="login-email" error={errors.email} required>
              <Input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                icon="ri-mail-line"
                placeholder="vous@exemple.bf"
                value={email}
                invalid={Boolean(errors.email)}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>

            <Field label="Mot de passe" htmlFor="login-password" error={errors.password} required>
              <PasswordInput
                id="login-password"
                name="password"
                autoComplete="current-password"
                placeholder="Votre mot de passe"
                value={password}
                invalid={Boolean(errors.password)}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm text-foreground-700">
                <input
                  type="checkbox"
                  name="remember"
                  className="h-4 w-4 rounded border-background-300"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Se souvenir de moi
              </label>
              <button
                type="button"
                onClick={onForgotPassword}
                disabled={resetState === "sending"}
                className="text-sm font-label font-medium text-primary-700 hover:text-primary-800 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resetState === "sending" ? "Envoi…" : "Mot de passe oublié ?"}
              </button>
            </div>

            {resetState === "sent" && resetMessage && (
              <Alert tone="success" className="mt-1">
                {resetMessage}
              </Alert>
            )}
            {resetState === "error" && resetMessage && (
              <Alert tone="error" className="mt-1">
                {resetMessage}
              </Alert>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 hover:bg-primary-600 hover:-translate-y-0.5 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
              )}
              {submitting ? "Connexion…" : "Se connecter"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-600">
            Pas encore de compte ?{" "}
            <Link to="/inscription" className="font-label font-medium text-primary-700 hover:text-primary-800">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20editorial%20photograph%20of%20a%20modern%20residential%20building%20facade%20in%20terracotta%20and%20cream%20tones%20with%20geometric%20balconies%2C%20soft%20golden%20hour%20light%2C%20minimal%20architecture%2C%20deep%20shadows%2C%20elegant%20composition%2C%20high%20detail&width=1000&height=1200&seq=login-immogest-01&orientation=portrait"
          alt="Immeuble résidentiel moderne aux tons chauds"
          title="Connexion à l'espace de gestion immobilière"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-transparent" />
        <div className="animate-fade-up absolute bottom-0 left-0 right-0 p-10" style={{ animationDelay: "160ms" }}>
          <p className="text-xl font-heading font-semibold text-background-50">
            « Je vois enfin clairement quels loyers sont encaissés et quels locataires sont en retard. »
          </p>
          <p className="mt-3 text-sm text-background-200/85">Aïcha Ouédraogo — Directrice d'agence, Bobo-Dioulasso</p>
        </div>
      </div>
    </div>
  );
}